import { Meta, Title } from '@solidjs/meta';
import {
	Button,
	Carousel,
	CarouselItem,
	Container,
	Heading,
	HorizontalRule,
	Link,
	MediaSection,
	MessageBar,
	Section,
	AccessCircleLogo,
	TextLink,
	TierContent,
	TierFlow,
	TierIcon,
	TierStep,
	Page,
	DialogTrigger,
	ProductCard,
	Picture,
} from '@troon/ui';
import { createAsync } from '@solidjs/router';
import { For, Match, Show, Suspense, Switch } from 'solid-js';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { IconInfo } from '@troon/icons/info';
import { IconChevronDown } from '@troon/icons/chevron-down';
import { NoHydration } from 'solid-js/web';
import { IconGift } from '@troon/icons/gift';
import { IconUserCircleCustom } from '@troon/icons/user-circle-custom';
import { IconFlagCustom } from '@troon/icons/flag-custom';
import { IconTagCustom } from '@troon/icons/tag-custom';
import { cachedGet } from '@troon/api-client';
import { IconMap } from '@troon/icons/map';
import { createContentfulRequest } from '../content/[model]/_client';
import { BenefitCard } from '../../components/benefit';
import { FrequentlyAskedQuestions } from '../../components/faqs';
import { getConfigValue } from '../../modules/config';
import { useUser } from '../../providers/user';
import { AuthDialog } from '../../partials/auth/dialog';
import { SubscriptionProducts } from '../../components/subscription-products';
import { PlanComparison } from '../../components/plan-comparison';
import { TopoBg } from '../../components/topo-bg';
import { AppDownloadUpsell } from '../../components/upsells/app-download';
import { Hero } from '../../components/hero/photo';
import { FacilitiesByGrouping } from '../../components/facility/by-grouping';
import type { RouteDefinition } from '@solidjs/router';

export default function AccessLanding() {
	const user = useUser();
	const facilities = createAsync(
		async () => {
			const all = await getFacilities({
				query: { location: {}, isBookable: true, sortBy: 'name' },
			});

			return all.facilities.filter((f) => f.supportsAccessPoints || f.supportsTroonAccess);
		},
		{ deferStream: true },
	);

	const benefitsData = createAsync(async () => getBenefits('access-landing-benefits'), { deferStream: true });

	return (
		<>
			<Title>Access Membership | Join Free or Upgrade to Premium for Exclusive Golf Rewards | Golf with Access</Title>
			<Meta
				name="description"
				content="Join Access free to earn points every round—or upgrade to Premium for exclusive tee time rates, faster rewards, and perks at 200+ courses."
			/>

			<Hero src={`${getConfigValue('IMAGE_HOST')}/hero/membership.jpg`}>
				<div class="flex flex-col gap-8">
					<div class="flex flex-col gap-4">
						<Heading as="h1">Access is the best way to play more golf.</Heading>
						<p>Earn every time you play. Unlock even more when you subscribe.</p>
					</div>
					<div class="flex flex-wrap justify-center gap-4">
						<Suspense>
							<Switch
								fallback={
									<>
										<AuthDialog key="auth-membership">
											<DialogTrigger class="size-fit grow-0">Join for free</DialogTrigger>
										</AuthDialog>
										<Button as={Link} href="#plans" appearance="tertiary-current" class="size-fit grow-0">
											View all Plans
										</Button>
									</>
								}
							>
								<Match when={user()?.troonAccessProductType}>
									<Button as={Link} href="/account/membership" appearance="tertiary-current" class="size-fit grow-0">
										Manage Membership
									</Button>
								</Match>
								<Match when={user()}>
									<Button as={Link} href="#plans" class="size-fit grow-0">
										Upgrade to Premium
									</Button>
									<Button as={Link} href="#plans" appearance="tertiary-current" class="size-fit grow-0">
										View all Plans
									</Button>
								</Match>
							</Switch>
						</Suspense>
					</div>
				</div>
			</Hero>

			<Page>
				<Container class="gap-16">
					<NoHydration>
						<Section>
							<Heading as="h2" class="text-center">
								How Access Works
							</Heading>

							<div class="grid w-full grid-cols-1 gap-8 text-center text-balance lg:grid-cols-3">
								<Section layout="tight">
									<IconUserCircleCustom class="mx-auto size-16 md:size-20" />
									<Heading as="h2" size="h5">
										Sign Up For Free
									</Heading>
									<p>
										Getting started with Access is free. Unlock instant Silver or Gold status with Access Premium, plus
										preferred rates on tee times.
									</p>
								</Section>

								<Section layout="tight">
									<IconFlagCustom class="mx-auto size-16 md:size-20" />
									<Heading as="h2" size="h5">
										Play & Earn
									</Heading>
									<p>
										Earn 1 Access Point for every dollar you spend on tee times at participating courses. Unlock point
										multipliers as you level-up your status.
									</p>
								</Section>

								<Section layout="tight">
									<IconTagCustom class="mx-auto size-16 md:size-20" />
									<Heading as="h2" size="h5">
										Redeem & Save
									</Heading>
									<p>
										Use your Access Points to book any available tee time online or in the app at participating courses
										— no restrictions, no hassle.
									</p>
								</Section>
							</div>
						</Section>

						<HorizontalRule />
					</NoHydration>

					<Container size="small" class="gap-4 text-center text-balance">
						<Heading as="h2" id="plans">
							Choose Your Access
						</Heading>
						<p class="text-lg">Earn every time you play. Unlock even more when you subscribe.</p>
					</Container>
					<div class="grid grid-cols-1 gap-8 *:nth-1:row-start-2 *:nth-2:row-start-1 lg:grid-cols-3 lg:flex-row-reverse lg:*:row-start-1! lg:*:nth-1:col-start-2 lg:*:nth-2:col-start-3 lg:*:nth-3:col-start-1">
						<SubscriptionProducts />
						<ProductCard
							name="Member"
							current={Boolean(user() && !user()?.troonAccessProductType)}
							description="Join for free and start earning points every time you play."
							benefits={['Earn 1 point on every dollar spent', 'Redeem points for free rounds, gear, and more']}
							price={<p class="text-xl font-semibold lg:text-5xl">Free</p>}
						>
							<Show when={!user()}>
								<AuthDialog key="auth-membership">
									<DialogTrigger appearance="secondary">Sign up for free</DialogTrigger>
								</AuthDialog>
							</Show>
						</ProductCard>
					</div>

					<details class="group flex flex-col gap-4">
						<Button as="summary" appearance="secondary" class="mx-auto size-fit">
							Compare all plans & Benefits <IconChevronDown class="transition-transform group-open:rotate-180" />
						</Button>
						<PlanComparison />
					</details>
				</Container>
				<NoHydration>
					<Container class="md:gap-16">
						<Section>
							<div class="relative flex w-full flex-wrap gap-4 rounded-lg bg-linear-to-r from-brand-900 to-brand-700 p-6 text-balance text-white sm:items-center sm:justify-between md:flex-nowrap md:p-8">
								<TopoBg placement="bottom" class="absolute inset-x-0 bottom-0 z-0 h-64 w-full text-white/50 md:h-32" />
								<div class="relative z-10 flex grow flex-col gap-8">
									<div class="flex flex-col gap-4">
										<Heading as="h2" size="h4">
											The perfect gift for the golfer in your life
										</Heading>
										<p>
											With savings at over 200 courses, a Premium membership is ultimate gift for the golfer in your
											life.
										</p>
									</div>
									<Button
										as={Link}
										href="/membership/gift"
										class="size-fit grow-0 after:absolute after:inset-0"
										size="sm"
									>
										Gift Access Premium <IconGift />
									</Button>
								</div>
								<Picture
									src={`${getConfigValue('IMAGE_HOST')}/hero/premium-cards-shadow.png`}
									width={1188}
									height={907}
									sizes="(min-width: 768px) 12rem, 50vw"
									alt=""
									class="relative z-10 order-first w-2/3 grow-0 self-start md:order-2 md:w-auto md:max-w-fit"
								/>
							</div>
						</Section>
					</Container>

					<Section appearance="secondary">
						<Container>
							<div class="grid grid-cols-1 gap-x-16 gap-y-8 md:grid-cols-2">
								<Section>
									<Heading as="h2">Play More. Earn More.</Heading>
									<p>
										Unlock elevated earning rates and exclusive benefits as you progress through Silver, Gold, and
										Platinum status levels.
									</p>
									<p class="font-semibold">
										Subscribe to Access Premium to get automatic Silver or Gold Status + unlock preferred rates and
										extra benefits.
									</p>

									<Show when={!user()}>
										<AuthDialog key="auth-membership">
											<DialogTrigger appearance="primary" class="size-fit grow-0">
												Join Access for Free
											</DialogTrigger>
										</AuthDialog>
									</Show>
									<p>
										Troon Rewards Member?{' '}
										<TextLink href="/account/transfer-points">
											Transfer Points <IconArrowRightMd />
										</TextLink>
									</p>
								</Section>

								<TierFlow>
									<TierStep>
										<TierIcon>
											<AccessCircleLogo color="brand" />
										</TierIcon>
										<TierContent title="Member">
											<p>Earn 1× points on every dollar. Start building status with no commitment.</p>
											<p class="font-bold">Book with points M-Thu anytime, F-Sun & holidays after 12pm</p>
										</TierContent>
									</TierStep>

									<TierStep>
										<TierIcon>
											<AccessCircleLogo color="silver" />
										</TierIcon>
										<TierContent title="Silver • 750 pts or Access Premium">
											<p>Earn 1.1x points on every dollar spent. No restrictions on redemption.</p>
											<Link
												href="/membership/checkout"
												class="flex w-full items-center justify-between gap-2 rounded-lg border-s-3 border-s-brand bg-white p-4 hover:bg-brand-50"
											>
												Get Silver Status instantly with Access Premium <IconArrowRightMd class="size-6 text-brand" />
											</Link>
										</TierContent>
									</TierStep>

									<TierStep>
										<TierIcon>
											<AccessCircleLogo color="gold" />
										</TierIcon>
										<TierContent title="Gold • 1,500 pts or Access Premium+">
											<p>Earn 1.15x points on every dollar spent. No restrictions on redemption.</p>
											<Link
												href="/membership/checkout"
												class="flex w-full items-center justify-between gap-2 rounded-lg border-s-3 border-s-brand bg-white p-4 hover:bg-brand-50"
											>
												Get Gold Status instantly with Access Premium+ <IconArrowRightMd class="size-6 text-brand" />
											</Link>
										</TierContent>
									</TierStep>

									<TierStep>
										<TierIcon>
											<AccessCircleLogo color="platinum" />
										</TierIcon>
										<TierContent title="Platinum • 3,000 pts">
											<p>Earn 1.2x points on every dollar spent. No restrictions on redemption.</p>
										</TierContent>
									</TierStep>
								</TierFlow>
							</div>

							<AppDownloadUpsell />
						</Container>
					</Section>
				</NoHydration>
			</Page>

			<Container>
				<Page>
					<Section>
						<div class="flex w-full flex-wrap items-center justify-between gap-x-32 gap-y-4">
							<div class="flex flex-col gap-4">
								<Heading as="h2">Explore Participating Courses</Heading>
								<p class="text-base text-balance md:text-lg">
									Simply book a tee time with any of our participating courses and complete your round to start earning
									rewards.
								</p>
							</div>
							<Button appearance="secondary" as={Link} href="/courses" class="size-fit grow-0">
								Full course directory <IconMap />
							</Button>
						</div>

						<Show when={facilities()}>{(facilities) => <FacilitiesByGrouping facilities={facilities()} />}</Show>
					</Section>

					<HorizontalRule />

					<Section class="relative">
						<Heading as="h2">Additional Perks for Premium Members</Heading>
						<p class="text-lg">Enjoy exclusive offers from your favorite brands to help you on and off the course.</p>

						<Suspense>
							<Carousel
								auto="play"
								description="Enjoy exclusive Access member benefits from your favorite brands."
								listClass="flex items-stretch -ms-4 xl:-ms-8"
								align="start"
								paginationClass="absolute -bottom-12 left-0 z-10"
								paginationButtonClass="text-neutral-400 aria-current:text-brand"
							>
								<For each={benefitsData()?.fields.benefits}>
									{(benefit) => {
										const { bannerImage, ...fields } = benefit!.fields;
										return (
											<CarouselItem class="min-w-0 basis-full ps-4 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4 xl:ps-8">
												<BenefitCard {...fields} level="h3" size="h4" class="h-full" />
											</CarouselItem>
										);
									}}
								</For>
							</Carousel>
						</Suspense>
					</Section>

					<HorizontalRule />
					<MediaSection src={`${getConfigValue('IMAGE_HOST')}/hero/access.jpg`} reverse>
						<Heading as="h2">Guest Passes</Heading>
						<p>
							Guest Passes allow your playing partners to play at the same discounted rate as you for a given round. All
							Premium members receive 3 Guest Passes per/year with no restrictions on when they can be used.
						</p>
						<MessageBar appearance="brand" icon={IconInfo}>
							<p>
								<b>Need more than 3 guest passes?</b> Unlimited Guest Passes are available as an add-on to either Access
								tier for $75/mo (billed annually).
							</p>
						</MessageBar>
						<Button as={Link} href="/membership/guest-passes" appearance="secondary" class="size-fit grow-0">
							Learn more
						</Button>
					</MediaSection>

					<HorizontalRule />

					<Section>
						<Container size="medium">
							<Heading as="h2" size="h2" class="text-center">
								Frequently asked questions
							</Heading>
							<div class="overflow-hidden rounded-lg border border-neutral">
								<FrequentlyAskedQuestions id="membership-faqs" />
							</div>
						</Container>
					</Section>
				</Page>
			</Container>
		</>
	);
}

const getBenefits = createContentfulRequest('benefits');
const getFacilities = cachedGet('/v0/facilities');

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
